import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, Loader2, User, Globe, Shield } from 'lucide-react';

const ChildSetup = () => {
  const { childId } = useParams();
  const isEditing = !!childId;
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    profile_avatar: '',
    has_disability: false,
    disability_notes: '',
    is_esl_learner: false,
    native_language: '',
    read_whole_page: true, // narrator-first mode; default ON, parents can disable
    math_curriculum: 'mixed',
    literacy_curriculum: 'mixed',
    kid_friendly_tone: true,
    accessibility_settings: {
      high_contrast: false,
      large_text: false,
      reduced_motion: false,
      voice_enabled: true,
      tts_enabled: true
    }
  });

  useEffect(() => {
    if (isEditing) {
      fetchChild();
    }
  }, [childId]);

  const fetchChild = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/children/${childId}`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load child profile');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await api.put(`/children/${childId}`, formData);
        toast.success('Profile updated successfully!');
      } else {
        await api.post('/children', formData);
        toast.success('Child profile created!');
      }
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save profile';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAccessibility = (key, value) => {
    setFormData(prev => ({
      ...prev,
      accessibility_settings: {
        ...prev.accessibility_settings,
        [key]: value
      }
    }));
  };

  const languages = [
    { value: 'spanish', label: 'Spanish' },
    { value: 'chinese', label: 'Chinese (Mandarin)' },
    { value: 'vietnamese', label: 'Vietnamese' },
    { value: 'arabic', label: 'Arabic' },
    { value: 'french', label: 'French' },
    { value: 'korean', label: 'Korean' },
    { value: 'tagalog', label: 'Tagalog' },
    { value: 'russian', label: 'Russian' },
    { value: 'portuguese', label: 'Portuguese' },
    { value: 'hindi', label: 'Hindi' },
    { value: 'other', label: 'Other' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="font-fredoka text-xl font-bold text-slate-800">
                {isEditing ? 'Edit Child Profile' : 'Add New Child'}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-sky-500" />
                Basic Information
              </CardTitle>
              <CardDescription>Enter your child's basic details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Child's Name</Label>
                <Input
                  id="name"
                  placeholder="Enter name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  required
                  data-testid="child-name-input"
                  className="h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="birth_date">Birth Date</Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => updateField('birth_date', e.target.value)}
                  required
                  data-testid="child-birthdate-input"
                  className="h-12"
                />
                <p className="text-sm text-slate-500">This helps us provide age-appropriate activities</p>
              </div>
            </CardContent>
          </Card>

          {/* ESL Support */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-green-500" />
                Language Support
              </CardTitle>
              <CardDescription>For English language learners</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">English Language Learner (ESL/ELL)</p>
                  <p className="text-sm text-slate-500">Enable simpler instructions and visual aids</p>
                </div>
                <Switch
                  checked={formData.is_esl_learner}
                  onCheckedChange={(checked) => updateField('is_esl_learner', checked)}
                  data-testid="esl-toggle"
                />
              </div>
              
              {formData.is_esl_learner && (
                <div className="space-y-2">
                  <Label htmlFor="native_language">Native Language</Label>
                  <Select
                    value={formData.native_language}
                    onValueChange={(value) => updateField('native_language', value)}
                  >
                    <SelectTrigger className="h-12" data-testid="native-language-select">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map(lang => (
                        <SelectItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Accessibility */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-500" />
                Accessibility & Special Needs
              </CardTitle>
              <CardDescription>Customize the learning experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Learning Disability Support</p>
                  <p className="text-sm text-slate-500">Enable additional accommodations</p>
                </div>
                <Switch
                  checked={formData.has_disability}
                  onCheckedChange={(checked) => updateField('has_disability', checked)}
                  data-testid="disability-toggle"
                />
              </div>

              {formData.has_disability && (
                <div className="space-y-2">
                  <Label htmlFor="disability_notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="disability_notes"
                    placeholder="Describe any specific needs or accommodations..."
                    value={formData.disability_notes}
                    onChange={(e) => updateField('disability_notes', e.target.value)}
                    data-testid="disability-notes-textarea"
                    className="min-h-[100px]"
                  />
                </div>
              )}

              <div className="border-t pt-4 space-y-4">
                <p className="font-medium text-slate-700">Curriculum preferences</p>
                <p className="text-xs text-slate-500 -mt-2">
                  Pick the approach that matches your homeschool or classroom. Lessons will adapt tone and structure to match.
                </p>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-sm">Math curriculum</Label>
                    <select
                      className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                      value={formData.math_curriculum}
                      onChange={(e) => updateField('math_curriculum', e.target.value)}
                      data-testid="math-curriculum-select"
                    >
                      <option value="mixed">Mixed / any</option>
                      <option value="eureka">Eureka Math (Great Minds)</option>
                      <option value="envision">enVision Math</option>
                      <option value="im">Illustrative Mathematics (IM K-5)</option>
                      <option value="kiddom">Kiddom</option>
                      <option value="zearn">Zearn</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm">Literacy curriculum</Label>
                    <select
                      className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                      value={formData.literacy_curriculum}
                      onChange={(e) => updateField('literacy_curriculum', e.target.value)}
                      data-testid="literacy-curriculum-select"
                    >
                      <option value="mixed">Mixed / any</option>
                      <option value="fundations">Wilson Fundations</option>
                      <option value="lexia_core5">Lexia Core5</option>
                      <option value="fishtank">Fishtank ELA</option>
                      <option value="education_literacy">Wit & Wisdom / Education Literacy</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-start justify-between gap-4 bg-amber-50/70 border border-amber-200 rounded-lg p-3">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      Kid-friendly tone
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">Lexia Core5 style</span>
                    </p>
                    <p className="text-sm text-slate-600">
                      Short sentences, cheerful voice, one warm emoji per section, celebrates effort. Recommended for kids under 8.
                    </p>
                  </div>
                  <Switch
                    checked={formData.kid_friendly_tone !== false}
                    onCheckedChange={(checked) => updateField('kid_friendly_tone', checked)}
                    data-testid="kid-friendly-tone-toggle"
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <p className="font-medium text-slate-700">Display Settings</p>

                <div className="flex items-start justify-between gap-4 bg-emerald-50/60 border border-emerald-200 rounded-lg p-3">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      Read the whole page
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded">Narrator-first</span>
                    </p>
                    <p className="text-sm text-slate-600">
                      Auto-reads every concept, question, and prompt aloud when it opens. Recommended for kids under 8. Turn off for older readers who prefer to read silently.
                    </p>
                  </div>
                  <Switch
                    checked={formData.read_whole_page !== false}
                    onCheckedChange={(checked) => updateField('read_whole_page', checked)}
                    data-testid="read-whole-page-toggle"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">High Contrast Mode</p>
                    <p className="text-sm text-slate-500">Increase visual contrast</p>
                  </div>
                  <Switch
                    checked={formData.accessibility_settings.high_contrast}
                    onCheckedChange={(checked) => updateAccessibility('high_contrast', checked)}
                    data-testid="high-contrast-toggle"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Large Text</p>
                    <p className="text-sm text-slate-500">Increase text size</p>
                  </div>
                  <Switch
                    checked={formData.accessibility_settings.large_text}
                    onCheckedChange={(checked) => updateAccessibility('large_text', checked)}
                    data-testid="large-text-toggle"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Reduced Motion</p>
                    <p className="text-sm text-slate-500">Minimize animations</p>
                  </div>
                  <Switch
                    checked={formData.accessibility_settings.reduced_motion}
                    onCheckedChange={(checked) => updateAccessibility('reduced_motion', checked)}
                    data-testid="reduced-motion-toggle"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Voice Input</p>
                    <p className="text-sm text-slate-500">Enable speaking answers</p>
                  </div>
                  <Switch
                    checked={formData.accessibility_settings.voice_enabled}
                    onCheckedChange={(checked) => updateAccessibility('voice_enabled', checked)}
                    data-testid="voice-input-toggle"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Text-to-Speech</p>
                    <p className="text-sm text-slate-500">Read content aloud</p>
                  </div>
                  <Switch
                    checked={formData.accessibility_settings.tts_enabled}
                    onCheckedChange={(checked) => updateAccessibility('tts_enabled', checked)}
                    data-testid="tts-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Link to="/dashboard" className="flex-1">
              <Button type="button" variant="outline" className="w-full h-12" data-testid="cancel-btn">
                Cancel
              </Button>
            </Link>
            <Button 
              type="submit" 
              className="flex-1 h-12 bg-sky-500 hover:bg-sky-600" 
              disabled={saving}
              data-testid="save-child-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Create Profile'
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ChildSetup;
